import React, { useEffect, useState } from "react";
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from "../utils/axiosInstance";
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Typography, Paper, IconButton,
} from "@mui/material";
import { Add, Edit, Delete, ExpandMore, ChevronRight } from "@mui/icons-material";
import { DndContext, PointerSensor, useSensor, useSensors, useDroppable, pointerWithin, rectIntersection, MeasuringStrategy } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API_BASE = "/category";

function SortableRow({ cat, expanded, toggle, hasChildren, onAdd, onEdit, onDelete, }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: cat.category_id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box sx={{ mb: 0.5, position: "relative" }}>
            <Paper ref={setNodeRef} style={style} sx={{
                p: 1, display: "flex", alignItems: "center", gap: 1,
                border: "1px solid", borderColor: "divider"
            }}>
                <Box {...attributes}{...listeners} sx={{ cursor: "grab", px: 1, userSelect: "none" }}> ☰</Box>
                <Typography sx={{ flexGrow: 1 }}>{cat.category_name}
                    {hasChildren && (
                        <IconButton size="small" onClick={() => toggle(cat.category_id)} sx={{ ml: 1 }}>
                            {expanded ? (
                                <ExpandMore fontSize="small" />
                            ) : (
                                <ChevronRight fontSize="small" />
                            )}
                        </IconButton>
                    )}
                </Typography>
                <IconButton size="small" onClick={() => onAdd(cat.category_id)}><Add fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => onEdit(cat)}><Edit fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => onDelete(cat)}><Delete fontSize="small" /></IconButton>
            </Paper>
            <DropZone id={`drop-${cat.category_id}`} />
        </Box>
    );
}

function DropZone({ id }) {
    const { setNodeRef, isOver } = useDroppable({ id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                position: "absolute",
                bottom: -6,
                left: 0,
                width: "100%",
                height: 8,
                backgroundColor: isOver ? "action.selected" : "transparent",
                pointerEvents: "none",
            }}
        />
    );
}

export default function CategoryManagementPage() {
    const sensors = useSensors(useSensor(PointerSensor, { distance: 5 }));
    const [categories, setCategories] = useState([]);
    const [expandedIds, setExpandedIds] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState("");
    const [parentId, setParentId] = useState(null);
    const [error, setError] = useState(false);
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await axiosInstance.get(API_BASE);
            setCategories(res.data ?? []);
            setError(false);
        } catch (err) {
            console.error(err);
            setCategories([]);
            setError(true);
        }
    };

    const childrenOf = (pid) =>
        categories
            .filter((c) => c.parent_category_id === pid)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    const customCollisionDetection = (args) => {
        // ① DropZone だけを対象にする
        const dropZones = args.droppableContainers.filter(
            (c) => typeof c.id === "string" && c.id.startsWith("drop-")
        );

        // ② DropZone に当たっていればそれを返す
        const dropZoneOver = pointerWithin({
            ...args,
            droppableContainers: dropZones,
        });

        if (dropZoneOver.length > 0) {
            return dropZoneOver;
        }

        // ③ それ以外は Sortable 用
        return rectIntersection(args);
    };
    const toggleExpand = (id) => {
        setExpandedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const isDescendant = (parentId, childId) => {
        let current = childId;
        while (current != null) {
            if (current === parentId) return true;
            const node = categories.find((c) => c.category_id === current);
            current = node ? node.parent_category_id : null;
        }
        return false;
    };

    const handleDragEnd = async ({ active, over, delta }) => {
        if (!active) return;

        const activeCat = categories.find(c => c.category_id === active.id);
        if (!activeCat) return;

        const shiftX = delta?.x ?? 0;

        // ===== 左右ドラッグで階層変更 =====
        if (shiftX > 20) {
            // 右にスライド → 左の兄弟の子にする
            const leftSibling = categories
                .filter(c => c.parent_category_id === activeCat.parent_category_id && c.category_id !== activeCat.category_id)
                .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                .reverse()
                .find(c => (c.sort_order ?? 0) < (activeCat.sort_order ?? 0));

            const newParent = leftSibling?.category_id ?? null;

            // 親を自身にしたり、子孫にしたりしない
            if (newParent !== null && !isDescendant(activeCat.category_id, newParent)) {
                try {
                    await axiosInstance.put(`${API_BASE}/${activeCat.category_id}`, {
                        parent_category_id: newParent,
                    });
                    /*setCategories(prev =>
                        prev.map(c => c.category_id === activeCat.category_id ? { ...c, parent_category_id: newParent } : c)
                    );*/
                } catch (e) {
                    console.error("階層変更エラー", e);
                } finally {
                    fetchCategories()
                    return;
                }
            }
        } else if (shiftX < -20) {
            // 左にスライド → 親の階層に戻す
            const parent = categories.find(c => c.category_id === activeCat.parent_category_id);
            const newParent = parent?.parent_category_id ?? null;

            // 親を自身にしたり、子孫にしたりしない
            if (newParent !== activeCat.category_id && !isDescendant(activeCat.category_id, newParent)) {
                try {
                    await axiosInstance.put(`${API_BASE}/${activeCat.category_id}`, {
                        parent_category_id: newParent,
                    });
                    /*setCategories(prev =>
                        prev.map(c => c.category_id === activeCat.category_id ? { ...c, parent_category_id: newParent } : c)
                    );*/
                } catch (e) {
                    console.error("階層変更エラー", e);
                } finally {
                    fetchCategories()
                    return;
                }
            }
        }

        // ===== DropZone にドロップ =====
        if (over && typeof over.id === "string" && over.id.startsWith("drop-")) {
            const parts = over.id.split("-");
            const parentRaw = parts[1];
            const toParent = parentRaw === "root" ? null : Number(parentRaw);

            if (toParent !== activeCat.parent_category_id &&
                toParent !== activeCat.category_id &&
                !Number.isNaN(toParent) &&
                !isDescendant(activeCat.category_id, toParent)
            ) {
                try {
                    await axiosInstance.put(`${API_BASE}/${activeCat.category_id}`, {
                        parent_category_id: toParent,
                    });
                } catch (e) {
                    console.error("DropZone階層変更エラー", e);
                } finally {
                    fetchCategories()
                }
            }
            return;
        }

        // ===== 同一親内の上下並び替え =====
        if (!over) return;
        const overCat = categories.find(c => c.category_id === over.id);
        if (!overCat) return;
        if (activeCat.parent_category_id !== overCat.parent_category_id) return;

        const siblings = childrenOf(activeCat.parent_category_id);
        const oldIndex = siblings.findIndex(c => c.category_id === activeCat.category_id);
        const newIndex = siblings.findIndex(c => c.category_id === overCat.category_id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = [...siblings];
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);

        setCategories(prev => {
            const others = prev.filter(c => c.parent_category_id !== activeCat.parent_category_id);
            return [...others, ...reordered];
        });

        try {
            await axiosInstance.put(`${API_BASE}/sort`, {
                updates: reordered.map((c, i) => ({
                    category_id: c.category_id,
                    sort_order: i,
                })),
            });
        } catch (e) {
            console.error("並び替えエラー", e);
        }

        fetchCategories();
    };

    const openAdd = (pid = null) => {
        setEditing(null);
        setName("");
        setParentId(pid);
        setDialogOpen(true);
    };

    const openEdit = (cat) => {
        setEditing(cat);
        setName(cat.category_name);
        setParentId(cat.parent_category_id);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (error) return; // エラーがある場合は保存禁止

        try {
            if (editing) {
                await axiosInstance.put(`${API_BASE}/${editing.category_id}`, {
                    category_name: name,
                });
            } else {
                await axiosInstance.post(API_BASE, {
                    category_name: name,
                    parent_category_id: parentId,
                });
            }
            setDialogOpen(false);
            fetchCategories();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (cat) => {
        const isRoot = cat.parent_category_id == null;

        const message = isRoot
            ? `${cat.category_name} を削除しますか？\n※このカテゴリに紐づく商品のカテゴリー設定は解除されます`
            : `${cat.category_name} を削除しますか？`;

        if (!window.confirm(message)) return;

        try {
            await axiosInstance.delete(`${API_BASE}/${cat.category_id}`);
            fetchCategories();
        } catch (err) {
            // サーバーから返ってきたメッセージを表示
            alert(err.response.data.message || '削除に失敗しました');
        }
    };


    const renderTree = (pid = null) => {
        const items = childrenOf(pid);

        return (
            <Box pl={pid ? 4 : 0}>
                {/* SortableContext は子がいてもいなくても作る */}
                <SortableContext
                    items={items.map((i) => i.category_id)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((cat) => (
                        <React.Fragment key={cat.category_id}>
                            <SortableRow
                                cat={cat}
                                expanded={expandedIds.includes(cat.category_id)}
                                toggle={toggleExpand}
                                hasChildren={childrenOf(cat.category_id).length > 0}
                                onAdd={openAdd}
                                onEdit={openEdit}
                                onDelete={handleDelete}
                            />

                            {expandedIds.includes(cat.category_id) &&
                                renderTree(cat.category_id)}
                        </React.Fragment>
                    ))}
                </SortableContext>
            </Box>
        );
    };
    if (error) {
        return <GlobalError errorMessage="カテゴリ取得に失敗しました" />;
    }

    return (
        <>
            <Typography variant="h1">カテゴリ管理</Typography>
            <Paper variant="section">
                <DndContext
                    sensors={sensors}
                    collisionDetection={customCollisionDetection}
                    measuring={{
                        droppable: {
                            strategy: MeasuringStrategy.Always,
                        },
                    }}
                    onDragEnd={handleDragEnd}
                >
                    {categories.length === 0 ? (
                        <Typography>データがありません</Typography>
                    ) : (
                        renderTree(null)
                    )}
                </DndContext>

                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
                    <DialogTitle>{editing ? "カテゴリ編集" : "カテゴリ追加"}</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="カテゴリ名"
                            margin="normal"
                            value={name}
                            onChange={(e) => {
                                const value = e.target.value;
                                setName(value);
                                setError(value.length > 255);
                            }}
                            error={error}
                            helperText={error ? "カテゴリ名は255文字以内で入力してください" : ""}
                            inputProps={{ maxLength: 255 }}
                            multiline
                            minRows={1}
                            maxRows={4}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)} variant="outlined">キャンセル</Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!name || error}
                        >
                            保存
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
            <Button startIcon={<Add />} variant="contained" sx={{ mt: 1 }} onClick={() => openAdd(null)}>追加</Button>
        </>
    );
}
